/**
 * The manufacturer of a tyre or rim, as a shopper would write it. P25009-377.
 *
 * `publicData.brand` holds a Console enum key, so it arrives camelCased
 * ("nokianTyres", "kumhoTire"), and the enum has an "I don't know" option
 * called `other` that roughly four out of five listings sit on. `other` is not
 * a manufacturer, so it resolves to '' here — and a caller must then leave the
 * brand out altogether rather than print a stand-in.
 *
 * What it must never fall back to is the car the parts fit. The listing page's
 * structured data used to do exactly that, so Google was told a used Continental
 * wheel set was a Volvo-brand product. That is the defect behind AC-1.
 *
 * The key -> name map is shared with the API server and the importer:
 * src/util/brandLabelCore.js (P25009-478 BR-2). Add a brand there.
 */
import core from './brandLabelCore';

/* Re-exported as consts, not `export { … } from`: the core is CommonJS, and a
 * clean webpack build rejects a named re-export of it (P25009-484). */

/** True for a stored value that names no manufacturer. */
export const isPlaceholderBrand = core.isPlaceholderBrand;

/**
 * @param {String} value publicData.brand
 * @return {String} a manufacturer name safe to publish, or '' when the listing
 *   does not really name one. Treat '' as "say nothing about the brand".
 */
export const brandLabel = core.brandLabel;

/** "Nytt 16\" kumhoTire" -> "Nytt 16\" Kumho": every raw brand key in a text
 * replaced by the brand's name (P25009-478). */
export const replaceRawBrandKeys = core.replaceRawBrandKeys;

/**
 * The brand as the page should print it (P25009-482 BR-11 / AC-6): the
 * Console enum's own label when it has one, otherwise the shared map's name
 * for the key ("kumhoTire" -> "Kumho"). A listing can carry a key the hosted
 * enum does not list (yet) — then the enum lookup finds nothing, and the raw
 * key must still never reach the screen.
 *
 * The enum is matched without regard to case. Live and Test were set up apart
 * and do not spell every option the same way ("gtradial" on Live, the ticket's
 * "gtRadial"), and a listing must not lose its label over a capital letter.
 *
 * @param {String} value publicData.brand
 * @param {Array} listingFields the listing-field config (for the enum labels)
 * @return {String} '' when the listing names no real manufacturer
 */
export const brandDisplayName = (value, listingFields) => {
  const raw = String(value == null ? '' : value).trim();
  if (!raw || isPlaceholderBrand(raw)) return '';
  const brandField = Array.isArray(listingFields)
    ? listingFields.find(field => field?.key === 'brand')
    : null;
  const wanted = raw.toLowerCase();
  const options = brandField?.enumOptions || [];
  // An exact match wins; only then a match that differs in case.
  const option =
    options.find(o => `${o.option}` === raw) ||
    options.find(o => `${o.option}`.toLowerCase() === wanted);
  const optionLabel = option?.label;
  return optionLabel && !isPlaceholderBrand(optionLabel) ? optionLabel : brandLabel(raw);
};
